/** Typer för sajtens redigerbara innehåll. Registret används av både admin och publika sidor. */

export type Locale = "sv" | "en" | "es";

/** Språk som alltid måste ha en standardtext i registret. */
export type BaseLocale = "sv" | "en";

/** En rad i ett listblock, t.ex. ett kort eller ett steg i en rutin. */
export type ContentItem = { title: string; text: string };

export type BlockFormat = "text" | "rich" | "list";

export type BlockValue = string | ContentItem[];

export type BlockDef = {
  /** Nyckel inom sidan, t.ex. "hero.title". */
  key: string;
  /** Etikett i admin. */
  label: string;
  format: BlockFormat;
  /** Hjälptext i admin. */
  hint?: string;
  /** Rekommenderad maxlängd, används för teckenräknaren. */
  max?: number;
  /** Egna fältetiketter för listblock, t.ex. { title: "Bild-URL", text: "Alt-text" }. */
  itemLabels?: { title: string; text: string };
  /** Ankare (id) på den publika sidan, används för "Visa på sajten". */
  anchor?: string;
  /** Standardtext per språk – visas när inget är sparat i databasen. */
  defaults: Record<BaseLocale, BlockValue> & Partial<Record<Locale, BlockValue>>;
};

export type PageDef = {
  /** Nyckel i databasen, t.ex. "gemenskap". */
  key: string;
  /** Namn i admin. */
  label: string;
  /** Gruppering i admin-listan. */
  group: string;
  /** Publika adresser per språk, används för förhandsgranskning. */
  paths: Record<BaseLocale, string> & Partial<Record<Locale, string>>;
  /** Sidan får aldrig visa kodtext eller ett annat språk när sparad text saknas. */
  noFallback?: boolean;
  blocks: BlockDef[];
};

/** Uppslagsbord: sidnyckel → blocknyckel → språk → värde. */
export type ContentMap = Record<string, Record<string, Partial<Record<Locale, BlockValue>>>>;

export function isItemList(value: unknown): value is ContentItem[] {
  return (
    Array.isArray(value) &&
    value.every(
      (v) =>
        !!v &&
        typeof v === "object" &&
        typeof (v as ContentItem).title === "string" &&
        typeof (v as ContentItem).text === "string",
    )
  );
}

/** Textformat i admin för listblock: en rad per post, "Rubrik | beskrivning". */
export function itemsToText(items: ContentItem[]): string {
  return items.map((i) => (i.text ? `${i.title} | ${i.text}` : i.title)).join("\n");
}

export function textToItems(text: string): ContentItem[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const i = line.indexOf("|");
      if (i === -1) return { title: line, text: "" };
      return { title: line.slice(0, i).trim(), text: line.slice(i + 1).trim() };
    });
}
